/**
 * topDownContext — emits the five Top-Down Coach observations from a
 * single trade's MultiTfState snapshot.
 *
 * Bundles topdown-aligned, topdown-counter-bias, topdown-zone-approach,
 * topdown-alwaysin-state, topdown-stage-context into ONE analyzer
 * because they all share the same upstream input (`trade.barAnalysis.
 * multiTf` + `trade.barAnalysis.keyLevels`) and would otherwise each
 * recompute the same `synthesizeMtfContext` call. One synthesis →
 * multiple observation candidates → emitted as separate observations
 * with stable IDs so each can be independently muted from the UI.
 *
 * Naming note: the Top-Down design doc §4 lists `topdown-alwaysin-flip`
 * and `topdown-stage-shift` — temporal "just changed" observations that
 * require streaming state comparisons across polling ticks. Trade-scope
 * analyzers run on a SINGLE trade's snapshot at entry, so the temporal
 * flip-detection isn't possible here. Renamed to `topdown-alwaysin-state`
 * and `topdown-stage-context` for v1 — they describe the ENTRY context
 * rather than a recent change. Live-polling versions (true flip
 * detection) will get added in a sibling LiveCoachFeed analyzer later.
 *
 * Composes with the v7.5.14 live-trade gate (these observations make
 * single-trade CONTEXT claims, not cohort outcome claims, so the gate
 * doesn't apply) and the v7.5.13 direction-cohort contract (no
 * direction-blind cohorts built here).
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../../types.js';
import { synthesizeMtfContext } from '../../../topDown/mtfSynthesis.js';

export const key = 'topDownContext';
export const tier = 'free';

/** Tolerance (in price points) for "near a key level" — same default
 *  as the AVWAP confluence zones for consistency. */
const NEAR_LEVEL_TOL_POINTS = 1.0;

/** Compose a short human-readable summary of bias / zone / trigger. */
const summarizeContext = (ctx) => {
  const parts = [];
  if (ctx.bias?.direction && ctx.bias.direction !== 'unclear') {
    parts.push(`HTF bias ${ctx.bias.direction.toUpperCase()}`);
  }
  if (ctx.zone?.type && ctx.zone.type !== 'unclear') {
    parts.push(`zone: ${ctx.zone.type}`);
  }
  if (ctx.trigger?.status === 'fired' && ctx.trigger.direction) {
    parts.push(`5M trigger ${ctx.trigger.direction.toUpperCase()}`);
  } else if (ctx.trigger?.status === 'forming' && ctx.trigger.direction) {
    parts.push(`5M trigger forming ${ctx.trigger.direction}`);
  }
  return parts.join(' · ');
};

/**
 * Detect entry within tolerance of any tracked key level. Returns the
 * nearest level + name + distance, or null. Used by zone-approach.
 */
const findNearestKeyLevel = (entryPrice, keyLevels, tolerancePoints) => {
  if (!Number.isFinite(entryPrice) || !keyLevels) return null;
  const candidates = [];
  const push = (name, value) => {
    const v = Number(value);
    if (Number.isFinite(v)) candidates.push({ name, value: v, distance: Math.abs(entryPrice - v) });
  };
  push('Golden Crayon', keyLevels.goldenCrayon);
  push('Previous Day High', keyLevels.prevDayHigh);
  push('Previous Day Low', keyLevels.prevDayLow);
  push('Previous Day Close', keyLevels.prevDayClose);
  push("Today's Open", keyLevels.todayOpen);
  push('All-Time High', keyLevels.allTimeHigh);
  push('Previous ATH', keyLevels.previousAth);
  push('Overnight High', keyLevels.overnightHigh);
  push('Overnight Low', keyLevels.overnightLow);
  // 4H body S/R: array of cluster centers
  if (Array.isArray(keyLevels.fourHourBodySR)) {
    keyLevels.fourHourBodySR.forEach((p, i) => push(`4H S/R #${i + 1}`, p));
  }
  // Initial Balance levels
  if (keyLevels.initialBalanceHigh != null) push('IB High', keyLevels.initialBalanceHigh);
  if (keyLevels.initialBalanceLow != null) push('IB Low', keyLevels.initialBalanceLow);

  const within = candidates.filter(c => c.distance <= tolerancePoints);
  if (within.length === 0) return null;
  // Closest first.
  within.sort((a, b) => a.distance - b.distance);
  return within[0];
};

// ── Observation builders ──────────────────────────────────────────────────

/**
 * topdown-aligned — fires when coherence is A+ or A.
 * SUCCESS-level affirmation that bias / zone / trigger all line up.
 */
const buildAlignedObservation = (mtfContext, trade) => {
  if (!mtfContext) return null;
  if (mtfContext.coherence !== 'A+' && mtfContext.coherence !== 'A') return null;
  const summary = summarizeContext(mtfContext);
  return makeObservation({
    id: `topdown-aligned:${trade?.id || 'unknown'}`,
    analyzer: 'topdown-aligned',
    scope: 'trade',
    scopeId: trade?.id,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.EXECUTION,
    title: `🟢 MTF alignment: ${mtfContext.coherence} — ${summary || 'all timeframes agree'}`,
    detail: mtfContext.coherenceRationale,
    evidence: { mtfContext },
    confidence: 0.85,
    priority: 80,
  });
};

/**
 * topdown-counter-bias — fires when coherence is D or F.
 * WARNING that the trade direction opposes the HTF bias. The F grade
 * specifically maps to the exact entry shape v7.5.13's direction-cohort
 * audit identified as the highest-risk pattern.
 */
const buildCounterBiasObservation = (mtfContext, trade) => {
  if (!mtfContext) return null;
  if (mtfContext.coherence !== 'D' && mtfContext.coherence !== 'F') return null;
  return makeObservation({
    id: `topdown-counter-bias:${trade?.id || 'unknown'}`,
    analyzer: 'topdown-counter-bias',
    scope: 'trade',
    scopeId: trade?.id,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `⚠️ Counter-bias entry (coherence ${mtfContext.coherence}) — ${mtfContext.bias?.direction?.toUpperCase() || '?'} bias vs ${mtfContext.trigger?.direction?.toUpperCase() || '?'} trigger`,
    detail: mtfContext.coherenceRationale,
    action: mtfContext.coherence === 'F'
      ? 'High-conviction HTF bias opposes the trigger. Size down or skip — this is the counter-trend chase pattern.'
      : 'Low-conviction HTF bias opposes the trigger. Manage risk tighter than usual; consider quicker exits.',
    evidence: { mtfContext },
    confidence: 0.82,
    priority: 88,
  });
};

/**
 * topdown-zone-approach — fires when entry price is within tolerance
 * of a tracked key level. INFO — context, not a warning.
 */
const buildZoneApproachObservation = (mtfContext, trade) => {
  const entryPrice = parseFloat(trade?.entry);
  if (!Number.isFinite(entryPrice)) return null;
  const keyLevels = trade?.barAnalysis?.keyLevels;
  if (!keyLevels) return null;
  const nearest = findNearestKeyLevel(entryPrice, keyLevels, NEAR_LEVEL_TOL_POINTS);
  if (!nearest) return null;
  return makeObservation({
    id: `topdown-zone-approach:${trade?.id || 'unknown'}:${nearest.name}`,
    analyzer: 'topdown-zone-approach',
    scope: 'trade',
    scopeId: trade?.id,
    severity: SEVERITY.INFO,
    category: CATEGORY.CONFLUENCE,
    title: `📍 Entry within ${nearest.distance.toFixed(2)} pts of ${nearest.name} (${nearest.value.toFixed(2)})`,
    detail: `Reactive level on the chart. ${mtfContext?.bias?.direction === 'long' && entryPrice >= nearest.value ? 'Above the level in an uptrend — support context.' : mtfContext?.bias?.direction === 'short' && entryPrice <= nearest.value ? 'Below the level in a downtrend — resistance context.' : 'Watch for reaction bar.'}`,
    evidence: { nearestLevel: nearest, mtfContext },
    confidence: 0.7,
    priority: 60,
  });
};

/**
 * topdown-alwaysin-state — describes the HTF always-in direction at
 * entry. INFO — context for the trade.
 */
const buildAlwaysInStateObservation = (mtfContext, trade) => {
  const fourHour = mtfContext?.bias?.dailyScore != null ? null : null;  // unused — kept for future flip detection
  void fourHour;
  // For v1: just describe the HTF state at entry.
  const hourly = trade?.barAnalysis?.multiTf?.oneHour;
  if (!hourly || hourly.alwaysIn === 'unclear') return null;
  const tradeDir = trade?.direction;
  const aligned = (tradeDir === 'long' && hourly.alwaysIn === 'long')
    || (tradeDir === 'short' && hourly.alwaysIn === 'short');
  return makeObservation({
    id: `topdown-alwaysin-state:${trade?.id || 'unknown'}`,
    analyzer: 'topdown-alwaysin-state',
    scope: 'trade',
    scopeId: trade?.id,
    severity: aligned ? SEVERITY.INFO : SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `🔄 1H always-in: ${hourly.alwaysIn.toUpperCase()}${tradeDir ? ` · trade is ${tradeDir.toUpperCase()}` : ''}`,
    detail: aligned
      ? 'Trade direction aligns with the 1H always-in read — momentum is with you on the intermediate timeframe.'
      : 'Trade direction opposes the 1H always-in read — you\'re fighting the intermediate momentum. Tighten stops accordingly.',
    evidence: { hourlyAlwaysIn: hourly.alwaysIn, tradeDirection: tradeDir },
    confidence: 0.72,
    priority: 65,
  });
};

/**
 * topdown-stage-context — describes the Wyckoff cycle stage at entry
 * on Daily + 4H. INFO — frames the regime the entry was taken in.
 */
const buildStageContextObservation = (mtfContext, trade) => {
  const daily = trade?.barAnalysis?.multiTf?.daily;
  const fourHour = trade?.barAnalysis?.multiTf?.fourHour;
  if (!daily || !fourHour) return null;
  if (daily.stage === 'unclear' && fourHour.stage === 'unclear') return null;
  return makeObservation({
    id: `topdown-stage-context:${trade?.id || 'unknown'}`,
    analyzer: 'topdown-stage-context',
    scope: 'trade',
    scopeId: trade?.id,
    severity: SEVERITY.INFO,
    category: CATEGORY.EXECUTION,
    title: `📈 Wyckoff stage at entry: D=${daily.stage}, 4H=${fourHour.stage}`,
    detail: `Daily is in the ${daily.stage} phase; 4H is in the ${fourHour.stage} phase. Each stage has different risk/reward dynamics for entries.`,
    evidence: { dailyStage: daily.stage, fourHourStage: fourHour.stage },
    confidence: 0.68,
    priority: 55,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

/**
 * Trade-scope analyzer. Emits 0–5 observations depending on what the
 * trade's MultiTfState supports.
 *
 * @param {Object} ctx       Coach scope context.
 * @param {Object} ctx.trade Single trade with barAnalysis populated.
 * @returns {Array<Observation>|null}
 */
export const analyze = (ctx) => {
  const trade = ctx?.trade;
  if (!trade) return null;
  const multiTf = trade?.barAnalysis?.multiTf;
  if (!multiTf || typeof multiTf !== 'object') return null;

  // One synthesis call powers all five observation builders.
  const mtfContext = synthesizeMtfContext(multiTf);

  const out = [];
  const aligned = buildAlignedObservation(mtfContext, trade);
  if (aligned) out.push(aligned);
  const counterBias = buildCounterBiasObservation(mtfContext, trade);
  if (counterBias) out.push(counterBias);
  const zoneApproach = buildZoneApproachObservation(mtfContext, trade);
  if (zoneApproach) out.push(zoneApproach);
  const alwaysIn = buildAlwaysInStateObservation(mtfContext, trade);
  if (alwaysIn) out.push(alwaysIn);
  const stageContext = buildStageContextObservation(mtfContext, trade);
  if (stageContext) out.push(stageContext);

  return out.length === 0 ? null : out;
};

// Test-friendly exports
export {
  buildAlignedObservation,
  buildCounterBiasObservation,
  buildZoneApproachObservation,
  buildAlwaysInStateObservation,
  buildStageContextObservation,
  findNearestKeyLevel,
  summarizeContext,
  NEAR_LEVEL_TOL_POINTS,
};
